import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon, ChevronRightIcon as ArrowRightIcon, ListIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SortableHeader } from '../ui/SortableHeader';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, getToday, getTomorrow, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { hasPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardRevenueByUser, DashboardRevenueByDoctor } from './DashboardRevenue';
import { DashboardAppointmentsByUser } from './DashboardAppointmentsByUser';
import { DashboardProductRevenue } from './DashboardProducts';
import { exportServiceDetailToPdf, exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { exportDashboardChartDataToExcel } from '../../utils/excelExport';
import { useSortableData } from '../../hooks/useSortableData';
import { useTheme } from '../../contexts/ThemeContext';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount
  } = useData();
  const {
    user
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const chartTooltipBg = isDark ? '#1e293b' : '#ffffff';
  const chartTooltipColor = isDark ? '#ffffff' : '#111827';
  const chartTooltipBorder = isDark ? '1px solid #334155' : '1px solid #e5e7eb';
  const chartTooltipLabelColor = isDark ? '#cbd5e1' : '#6b7280';
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const userRole = user?.role || '';
  const normalize = (name: string) => name ? name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
  const cleanRole = normalize(userRole);
  const canViewStats = hasPermission(userRole, 'dashboard_stats', 'read');
  const canViewCalendar = hasPermission(userRole, 'dashboard_calendar', 'read');
  const canViewPipeline = hasPermission(userRole, 'dashboard_pipeline', 'read');
  const canViewProducts = hasPermission(userRole, 'products', 'read');
  // Only admin, supervisor and recepcion can download PDFs/Excel of charts/details
  const canDownloadPdf = cleanRole === 'administrador' || cleanRole === 'supervisor' || cleanRole === 'recepcion' || cleanRole === 'recepcionista';
  // Detect if user is a doctor/terapeuta and find their doctor record
  const isDoctorUser = useMemo(() => {
    const role = userRole.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return role === 'doctor' || role === 'terapeuta';
  }, [userRole]);
  const userDoctorId = useMemo(() => {
    if (!isDoctorUser || !user) return null;
    const matchedDoctor = doctors.find(d => d.email?.toLowerCase().trim() === user.email?.toLowerCase().trim());
    return matchedDoctor?.id || null;
  }, [isDoctorUser, user, doctors]);
  const getManualPermission = (moduleKey: string) => {
    try {
      const saved = localStorage.getItem('savidna-role-permissions');
      if (!saved) return false;
      const perms = JSON.parse(saved);
      const level = perms[moduleKey]?.[cleanRole];
      return level === 'read' || level === 'full';
    } catch (e) {
      return false;
    }
  };
  const canViewRevenueByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_user', 'read') || getManualPermission('dashboard_revenue_user');
  const canViewRevenueByDoctor = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_doctor', 'read') || getManualPermission('dashboard_revenue_doctor');
  const canViewProductRevenue = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_product_revenue', 'read') || getManualPermission('dashboard_product_revenue');
  const canViewAppointmentsByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_appointments_by_user', 'read') || getManualPermission('dashboard_appointments_by_user');
  const canViewRecentAppointments = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_recent_appointments', 'read') || getManualPermission('dashboard_recent_appointments');
  const canViewServiceDetail = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_service_detail', 'read') || getManualPermission('dashboard_service_detail');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [doctorDateFrom, setDoctorDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [doctorDateTo, setDoctorDateTo] = useState<string>(getToday());
  const today = getToday();
  const selectedDateAppointments = useMemo(() => {
    const allApts = getAppointmentsByDate(selectedDate);
    if (isDoctorUser && userDoctorId) {
      return allApts.filter(a => a.doctorId === userDoctorId);
    }
    return allApts;
  }, [selectedDate, isDoctorUser, userDoctorId, getAppointmentsByDate]);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics (last 7 days trend only)
  const appointmentStats = useMemo(() => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      // Use timezone-aware date string instead of toISOString() which uses UTC
      const dateStr = date.toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric',
          timeZone: 'America/Santo_Domingo'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    return {
      last7Days
    };
  }, [appointments]);
  // Top Doctores por Citas - filtered by date range
  const doctorChartData = useMemo(() => {
    const filteredAppointments = appointments.filter(apt => {
      if (doctorDateFrom && apt.date < doctorDateFrom) return false;
      if (doctorDateTo && apt.date > doctorDateTo) return false;
      return true;
    });
    const byDoctor = filteredAppointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
  }, [appointments, getDoctor, doctorDateFrom, doctorDateTo]);
  // Service Detail Statistics
  const serviceDetailStats = useMemo(() => {
    const completedAppointments = appointments.filter(a => a.status === 'completada');
    const serviceMap: Record<string, {
      name: string;
      count: number;
      totalRevenue: number;
    }> = {};
    completedAppointments.forEach(apt => {
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      serviceIds.forEach(sId => {
        const service = getService(sId);
        if (service) {
          if (!serviceMap[service.id]) serviceMap[service.id] = {
            name: service.name,
            count: 0,
            totalRevenue: 0
          };
          serviceMap[service.id].count++;
          serviceMap[service.id].totalRevenue += service.price;
        }
      });
    });
    const grandTotalRevenue = Object.values(serviceMap).reduce((sum, s) => sum + s.totalRevenue, 0);
    const grandTotalCount = Object.values(serviceMap).reduce((sum, s) => sum + s.count, 0);
    const details = Object.values(serviceMap).map(s => ({
      ...s,
      avgPerAppointment: s.count > 0 ? s.totalRevenue / s.count : 0,
      percentOfTotal: grandTotalRevenue > 0 ? s.totalRevenue / grandTotalRevenue * 100 : 0
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);
    return {
      details,
      grandTotalRevenue,
      grandTotalCount
    };
  }, [appointments, getService]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'nuevo_lead',
    name: 'Nuevo Lead',
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400'
  }, {
    id: 'contactado',
    name: 'Contactado',
    color: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50 dark:bg-cyan-900/20',
    textColor: 'text-cyan-600 dark:text-cyan-400'
  }, {
    id: 'evaluacion_consulta',
    name: 'Evaluación / Consulta',
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
    textColor: 'text-purple-600 dark:text-purple-400'
  }, {
    id: 'cotizacion_enviada',
    name: 'Cotización Enviada',
    color: 'from-amber-500 to-amber-600',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-600 dark:text-amber-400'
  }, {
    id: 'confirmado',
    name: 'Confirmado',
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    textColor: 'text-emerald-600 dark:text-emerald-400'
  }, {
    id: 'cita_agendada',
    name: 'Cita Agendada',
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50 dark:bg-teal-900/20',
    textColor: 'text-teal-600 dark:text-teal-400'
  }, {
    id: 'servicio_realizado',
    name: 'Servicio Realizado',
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400'
  }];
  const getStageCount = (stage: string) => opportunities.filter(o => o.stage === stage).length;
  const getStageAmount = (stage: string) => opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      if (a.date !== dateStr || a.status === 'cancelada') return false;
      if (isDoctorUser && userDoctorId && a.doctorId !== userDoctorId) return false;
      return true;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  // Mapped recent appointments with resolved names for sorting
  const recentAppointmentsMapped = recentAppointments.map(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const service = getService(apt.serviceId);
    return {
      ...apt,
      patientName: patient?.name || '-',
      doctorName: doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-',
      serviceName: service?.name || '-'
    };
  });
  const {
    sortedItems: sortedRecentAppointments,
    requestSort: recentRequestSort,
    isSorted: recentIsSorted
  } = useSortableData(recentAppointmentsMapped);
  // Sortable service detail stats
  const {
    sortedItems: sortedServiceDetails,
    requestSort: serviceRequestSort,
    isSorted: serviceIsSorted
  } = useSortableData(serviceDetailStats.details);
  const formattedDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const getStatusAccentColor = (status: string) => {
    switch (status) {
      case 'programada':
        return 'bg-teal-500';
      case 'completada':
        return 'bg-emerald-500';
      case 'cancelada':
        return 'bg-rose-500';
      case 'reprogramada':
        return 'bg-amber-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="space-y-6" data-id="element-2317">
      {/* Welcome Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 md:p-8 text-white shadow-xl text-white" data-id="element-2318">
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-2319" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-2320" />
        {/* Patrón decorativo de spa (loto, hoja, gota, piedras zen) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
        opacity: 0.11
      }} aria-hidden="true" data-id="element-2321">
          <defs data-id="element-2322">
            <pattern id="spaPattern" width="160" height="160" patternUnits="userSpaceOnUse" patternTransform="rotate(6)" data-id="element-2323">
              <g fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2324">
                <g transform="translate(20,18)" data-id="element-2325">
                  <path d="M12 26 c-8-4-12-12-12-12 4-1 9 1 12 5 3-4 8-6 12-5 0 0-4 8-12 12z" data-id="element-2326" />
                  <path d="M12 26 c-4-7-3-16-3-16 3 1 5 5 5 9" data-id="element-2327" />
                  <path d="M12 26 c4-7 3-16 3-16 -3 1 -5 5 -5 9" data-id="element-2328" />
                </g>
                <g transform="translate(108,28)" data-id="element-2329">
                  <path d="M0 14 c0-10 8-14 16-14 0 10-6 16-16 14z" data-id="element-2330" />
                  <path d="M3 12 c4-3 8-6 11-9" data-id="element-2331" />
                </g>
                <g transform="translate(28,104)" data-id="element-2332">
                  <path d="M8 0 c5 7 8 11 8 15 a8 8 0 0 1 -16 0 c0-4 3-8 8-15z" data-id="element-2333" />
                </g>
                <g transform="translate(104,104)" data-id="element-2334">
                  <ellipse cx="12" cy="22" rx="14" ry="4.5" data-id="element-2335" />
                  <ellipse cx="12" cy="12" rx="10" ry="4" data-id="element-2336" />
                  <ellipse cx="12" cy="4" rx="6.5" ry="3" data-id="element-2337" />
                </g>
              </g>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#spaPattern)" data-id="element-2338" />
        </svg>
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-2339" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-2340" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-300/10 rounded-full blur-xl" data-id="element-2341" />
        <div className="relative z-10" data-id="element-2342">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-2343">
            <div data-id="element-2344">
              <div className="flex items-center gap-2 mb-2" data-id="element-2345">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-teal-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2346">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-2347" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-2348" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-2349" />
                </svg>
                <span className="text-white/75 text-sm font-medium" data-id="element-2350">
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-2351">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'},{' '}
                {user?.name?.split(' ')[0] || 'Usuario'}
              </h1>
              <p className="text-white/75 text-sm font-medium" data-id="element-2352">
                Panel de control de Savidna
              </p>
              <div className="flex flex-wrap gap-3 mt-4" data-id="element-2353">
                <button onClick={() => onNavigate('appointments:today')} className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20 hover:bg-white/25 transition-colors cursor-pointer" data-id="element-2354">
                  <ClockIcon className="w-4 h-4" data-id="element-2355" />
                  <span className="font-semibold" data-id="element-2356">{todayCount}</span>
                  <span className="text-white/75 text-sm font-medium" data-id="element-2357">citas hoy</span>
                </button>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20" data-id="element-2358">
                  <UsersIcon className="w-4 h-4" data-id="element-2359" />
                  <span className="font-semibold" data-id="element-2360">{patients.length}</span>
                  <span className="text-white/75 text-sm font-medium" data-id="element-2361">pacientes</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2362">
              {hasPermission(userRole, 'patients', 'full') && <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2364" />} onClick={() => onNavigate('patients:new')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2363">
                  Nuevo Paciente
                </Button>}
              {hasPermission(userRole, 'appointments', 'full') && <Button variant="ghost" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2366" />} onClick={() => onNavigate('appointments:new')} style={{
              backgroundColor: '#ffffff',
              backgroundImage: 'none',
              color: '#0f766e'
            }} className="shadow-lg shadow-teal-900/20 hover:opacity-90" data-id="element-2365">
                  Nueva Cita
                </Button>}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {canViewStats && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2367">
          {[{
        icon: CalendarIcon,
        value: totalAppointments,
        label: 'Citas Totales',
        gradient: 'from-teal-500 to-cyan-500',
        module: 'appointments'
      }, {
        icon: ClockIcon,
        value: todayCount,
        label: 'Citas Hoy',
        gradient: 'from-emerald-500 to-green-500',
        module: 'appointments:today'
      }, {
        icon: UserIcon,
        value: activeDoctors,
        label: 'Doctores',
        gradient: 'from-purple-500 to-indigo-500',
        module: 'doctors'
      }, {
        icon: BriefcaseIcon,
        value: totalServices,
        label: 'Servicios',
        gradient: 'from-orange-500 to-amber-500',
        module: 'services'
      }, {
        icon: UsersIcon,
        value: totalContacts,
        label: 'Contactos',
        gradient: 'from-yellow-500 to-amber-500',
        module: 'contacts'
      }, {
        icon: TrendingUpIcon,
        value: totalOpportunities,
        label: 'Oportunidades',
        gradient: 'from-rose-500 to-pink-500',
        module: 'opportunities'
      }].map((kpi, index) => {
        const Icon = kpi.icon;
        return <Card key={index} className="cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group" onClick={() => onNavigate(kpi.module)} data-id="element-2368">
                <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${kpi.gradient}`} data-id="element-2369" />
                <div className="flex items-center gap-3 pl-3" data-id="element-2370">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg`} data-id="element-2371">
                    <Icon className="w-5 h-5 text-white" data-id="element-2372" />
                  </div>
                  <div data-id="element-2373">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2374">
                      {kpi.value}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-2375">
                      {kpi.label}
                    </p>
                  </div>
                </div>
              </Card>;
      })}
        </div>}

      {/* Stats Charts */}
      {canViewStats && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2376">
          <Card padding="none" className="overflow-hidden" data-id="element-2377">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-2378" />
            <div className="p-4 md:p-6" data-id="element-2379">
              <CardHeader className="px-0 pt-0" data-id="element-2380">
                <div className="flex items-center justify-between" data-id="element-2381">
                  <CardTitle className="flex items-center gap-2" data-id="element-2382">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-2383">
                      <TrendingUpIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-2384" />
                    </div>
                    Tendencia Últimos 7 Días
                  </CardTitle>
                  {canDownloadPdf && <button onClick={() => {
                const cols = [{
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Total',
                  key: 'total'
                }, {
                  header: 'Completadas',
                  key: 'completadas'
                }, {
                  header: 'Programadas',
                  key: 'programadas'
                }, {
                  header: 'Canceladas',
                  key: 'canceladas'
                }];
                const chartRows = appointmentStats.last7Days.map(d => ({
                  label: d.date,
                  values: {
                    fecha: d.date,
                    total: d.total,
                    completadas: d.completadas,
                    programadas: d.programadas,
                    canceladas: d.canceladas
                  }
                }));
                exportDashboardChartDataToPdf(chartRows, cols, 'Tendencia Citas Últimos 7 Días');
              }} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2385">
                      <DownloadIcon className="w-4 h-4" data-id="element-2386" />
                    </button>}
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2387">
                <ResponsiveContainer width="100%" height={280} data-id="element-2388">
                  <AreaChart data={appointmentStats.last7Days} data-id="element-2389">
                    <defs data-id="element-2390">
                      <linearGradient id="colorCompletadas" x1="0" y1="0" x2="0" y2="1" data-id="element-2391">
                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} data-id="element-2392" />
                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} data-id="element-2393" />
                      </linearGradient>
                      <linearGradient id="colorProgramadas" x1="0" y1="0" x2="0" y2="1" data-id="element-2394">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-2395" />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} data-id="element-2396" />
                      </linearGradient>
                      <linearGradient id="colorCanceladas" x1="0" y1="0" x2="0" y2="1" data-id="element-2397">
                        <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-2398" />
                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-2399" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} vertical={false} data-id="element-2400" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2401" />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2402" />
                  <Tooltip contentStyle={{
                  backgroundColor: chartTooltipBg,
                  color: chartTooltipColor,
                  border: chartTooltipBorder,
                  borderRadius: '16px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  padding: '12px 16px'
                }} labelStyle={{
                  color: chartTooltipLabelColor
                }} formatter={(value, name) => [<span style={{
                  fontWeight: 'bold',
                  color: chartTooltipColor
                }} data-id="element-2404">{value}</span>, name]} data-id="element-2403" />
                
                    <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} data-id="element-2405" />
                    <Area type="monotone" dataKey="completadas" stroke="#14b8a6" strokeWidth={2} fill="url(#colorCompletadas)" name="Completadas" data-id="element-2406" />
                    <Area type="monotone" dataKey="programadas" stroke="#0ea5e9" strokeWidth={2} fill="url(#colorProgramadas)" name="Programadas" data-id="element-2407" />
                    <Area type="monotone" dataKey="canceladas" stroke="#f43f5e" strokeWidth={2} fill="url(#colorCanceladas)" name="Canceladas" data-id="element-2408" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden" data-id="element-2409">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-2410" />
            <div className="p-4 md:p-6" data-id="element-2411">
              <CardHeader className="px-0 pt-0" data-id="element-2412">
                <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-2413">
                  <CardTitle className="flex items-center gap-2" data-id="element-2414">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30" data-id="element-2415">
                      <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-2416" />
                    </div>
                    Top Doctores y Terapeutas por Citas
                  </CardTitle>
                  <div className="flex items-center gap-2" data-id="element-2417">
                    {canDownloadPdf && doctorChartData.length > 0 && <button onClick={() => {
                  const cols = [{
                    header: 'Doctor',
                    key: 'nombre'
                  }, {
                    header: 'Citas',
                    key: 'citas'
                  }];
                  const chartRows = doctorChartData.map(d => ({
                    label: d.name,
                    values: {
                      nombre: d.name,
                      citas: d.citas
                    }
                  }));
                  exportDashboardChartDataToPdf(chartRows, cols, 'Top Doctores y Terapeutas por Citas', {
                    from: doctorDateFrom,
                    to: doctorDateTo
                  });
                }} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2418">
                        <DownloadIcon className="w-4 h-4" data-id="element-2419" />
                      </button>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3" data-id="element-2420">
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2421">
                    <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateFrom} onChange={e => setDoctorDateFrom(e.target.value)} data-id="element-2422" />
                    <span className="text-gray-300 text-xs" data-id="element-2423">→</span>
                    <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateTo} onChange={e => setDoctorDateTo(e.target.value)} data-id="element-2424" />
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-2425">
                    {doctorChartData.reduce((sum, d) => sum + d.citas, 0)} citas
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2426">
                {doctorChartData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2427">
                    <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2428" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2429">
                      No hay datos en este rango
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2430">
                      Ajusta las fechas para ver resultados
                    </p>
                  </div> : <ResponsiveContainer width="100%" height={280} data-id="element-2431">
                    <BarChart data={doctorChartData} layout="vertical" data-id="element-2432">
                      <defs data-id="element-2433">
                        <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0" data-id="element-2434">
                          <stop offset="0%" stopColor="#8b5cf6" data-id="element-2435" />
                          <stop offset="100%" stopColor="#6366f1" data-id="element-2436" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal={true} vertical={false} data-id="element-2437" />
                      <XAxis type="number" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2438" />
                      <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={100} data-id="element-2439" />
                      <Tooltip contentStyle={{
                  backgroundColor: chartTooltipBg,
                  color: chartTooltipColor,
                  border: chartTooltipBorder,
                  borderRadius: '16px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                  padding: '12px 16px'
                }} labelStyle={{
                  color: chartTooltipLabelColor
                }} formatter={(value: number, name: string) => [<span style={{
                  fontWeight: 'bold',
                  color: chartTooltipColor
                }} data-id="element-2441">{value}</span>, name]} data-id="element-2440" />
                      <Bar dataKey="citas" fill="url(#barGradient)" radius={[0, 8, 8, 0]} name="Citas" data-id="element-2442" />
                    </BarChart>
                  </ResponsiveContainer>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Revenue Section */}
      {(canViewRevenueByUser || canViewRevenueByDoctor) && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2443">
          {canViewRevenueByUser && <DashboardRevenueByUser appointments={appointments} getService={getService} data-id="element-2444" />}
          {canViewRevenueByDoctor && <DashboardRevenueByDoctor appointments={appointments} doctors={doctors} getService={getService} getDoctor={getDoctor} data-id="element-2445" />}
        </div>}

      {/* Products Revenue Widget */}
      {canViewProductRevenue && <DashboardProductRevenue data-id="element-2446" />}

      {/* Pipeline */}
      {canViewPipeline && <Card padding="none" className="overflow-hidden" data-id="element-2447">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500" data-id="element-2448" />
          <div className="p-4 md:p-6" data-id="element-2449">
            <CardHeader className="px-0 pt-0" data-id="element-2450">
              <CardTitle className="flex items-center gap-2" data-id="element-2451">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30" data-id="element-2452">
                  <TrendingUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2453" />
                </div>
                Pipeline de Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2454">
              <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-2455">
                {pipelineStages.map((stage, index) => {
              const count = getStageCount(stage.id);
              const amount = getStageAmount(stage.id);
              return <div key={stage.id} className="flex items-center" data-id="element-2456">
                      <div className={`flex-1 min-w-[140px] p-4 ${stage.bgLight} rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden`} data-id="element-2457">
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stage.color}`} data-id="element-2458" />
                        <div className="flex items-center gap-2 mb-2" data-id="element-2459">
                          <div className={`w-2.5 h-2.5 rounded-full bg-gradient-to-r ${stage.color}`} data-id="element-2460" />
                          <span className={`text-sm font-semibold ${stage.textColor}`} data-id="element-2461">
                            {stage.name}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2462">
                          {count}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2463">
                          {formatCurrency(amount)}
                        </p>
                      </div>
                      {index < pipelineStages.length - 1 && <ArrowRightIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-1 flex-shrink-0" data-id="element-2464" />}
                    </div>;
            })}
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Appointments by User */}
      {canViewAppointmentsByUser && <DashboardAppointmentsByUser appointments={appointments} data-id="element-2465" />}

      {/* Calendar and Appointments */}
      {canViewCalendar && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2466">
          <Card padding="none" className="overflow-hidden" data-id="element-2467">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-2468" />
            <div className="p-4 md:p-6" data-id="element-2469">
              <CardHeader className="px-0 pt-0" data-id="element-2470">
                <div className="flex items-center justify-between" data-id="element-2471">
                  <CardTitle className="flex items-center gap-2" data-id="element-2472">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-2473">
                      <CalendarIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-2474" />
                    </div>
                    Calendario
                  </CardTitle>
                  <div className="flex items-center gap-1" data-id="element-2475">
                    <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-2476">
                      <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-2477" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[130px] text-center" data-id="element-2478">
                      {getMonthName(month)} {year}
                    </span>
                    <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-2479">
                      <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-2480" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2481">
                <div className="grid grid-cols-7 gap-1 mb-3" data-id="element-2482">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-400 dark:text-gray-500 py-2" data-id="element-2483">
                        {day}
                      </div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-id="element-2484">
                  {calendarDays.map((day, index) => {
                if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-2485" />;
                const dateStr = getDateString(day);
                const appointmentCount = getAppointmentCountForDay(day);
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`aspect-square rounded-xl text-sm font-medium relative flex flex-col items-center justify-center gap-0.5 transition-all duration-200
                          ${isToday ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' : ''}
                          ${isSelected && !isToday ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : ''}
                          ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-2486">
                        <span data-id="element-2487">{day}</span>
                        {appointmentCount > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-teal-500'}`} data-id="element-2488" />}
                      </button>;
              })}
                </div>
              </CardContent>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden" data-id="element-2489">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-2490" />
            <div className="p-4 md:p-6" data-id="element-2491">
              <CardHeader className="px-0 pt-0" data-id="element-2492">
                <CardTitle className="flex items-center gap-2" data-id="element-2493">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30" data-id="element-2494">
                    <ClockIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-2495" />
                  </div>
                  Citas de {selectedDate === today ? 'Hoy' : selectedDate}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2496">
                {selectedDateAppointments.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2497">
                    <CalendarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2498" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2499">
                      No hay citas programadas
                    </p>
                  </div> : <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1" data-id="element-2500">
                    {selectedDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden" data-id="element-2501">
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusAccentColor(apt.status)}`} data-id="element-2502" />
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-teal-500/20 flex-shrink-0" data-id="element-2503">
                            {patient?.name?.charAt(0).toUpperCase() || 'P'}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2504">
                            <div className="flex items-center gap-2 mb-1" data-id="element-2505">
                              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2506">
                                {formatTime(apt.time)}
                              </span>
                              <span className="text-sm text-gray-400" data-id="element-2507">•</span>
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2508">
                                {apt.duration} min
                              </span>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-2509">
                              {patient?.name || 'Paciente'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-2510">
                              {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}{' '}
                              • {service?.name}
                            </p>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-2511">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                  </div>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Recent Appointments */}
      {canViewRecentAppointments && <Card padding="none" className="overflow-hidden" data-id="element-2512">
          <div className="h-1 bg-gradient-to-r from-gray-400 to-gray-500" data-id="element-2513" />
          <div className="p-4 md:p-6" data-id="element-2514">
            <CardHeader className="px-0 pt-0" data-id="element-2515">
              <div className="flex items-center justify-between" data-id="element-2516">
                <CardTitle className="flex items-center gap-2" data-id="element-2517">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700" data-id="element-2518">
                    <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-2519" />
                  </div>
                  Citas Recientes
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-2520">
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToPdf(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2521">
                      <DownloadIcon className="w-4 h-4" data-id="element-2522" />
                    </button>}
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToExcel(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Descargar Excel" data-id="element-2523">
                      <FileSpreadsheetIcon className="w-4 h-4" data-id="element-2524" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300" data-id="element-2525">
                    Ver todas
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2526" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2527">
              <div className="overflow-x-auto" data-id="element-2528">
                <table className="w-full" data-id="element-2529">
                  <thead data-id="element-2530">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2531">
                      <SortableHeader label="Paciente" sortKey="patientName" currentDirection={recentIsSorted('patientName')} onSort={recentRequestSort} data-id="element-2532" />
                      <SortableHeader label="Doctor" sortKey="doctorName" currentDirection={recentIsSorted('doctorName')} onSort={recentRequestSort} data-id="element-2533" />
                      <SortableHeader label="Servicio" sortKey="serviceName" currentDirection={recentIsSorted('serviceName')} onSort={recentRequestSort} data-id="element-2534" />
                      <SortableHeader label="Fecha" sortKey="date" currentDirection={recentIsSorted('date')} onSort={recentRequestSort} data-id="element-2535" />
                      <SortableHeader label="Hora" sortKey="time" currentDirection={recentIsSorted('time')} onSort={recentRequestSort} data-id="element-2536" />
                      <SortableHeader label="Estado" sortKey="status" currentDirection={recentIsSorted('status')} onSort={recentRequestSort} data-id="element-2537" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2538">
                    {sortedRecentAppointments.map(apt => <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2539">
                        <td className="py-3 px-4" data-id="element-2540">
                          <div className="flex items-center gap-3" data-id="element-2541">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2542">
                              {apt.patientName?.charAt(0).toUpperCase() || 'P'}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2543">
                              {apt.patientName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2544">
                          {apt.doctorName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2545">
                          {apt.serviceName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2546">
                          {apt.date}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2547">
                          {formatTime(apt.time)}
                        </td>
                        <td className="py-3 px-4" data-id="element-2548">
                          <div className="flex items-center gap-2" data-id="element-2549">
                            <div className={`w-2 h-2 rounded-full ${getStatusAccentColor(apt.status)}`} data-id="element-2550" />
                            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2551">
                              {getStatusName(apt.status)}
                            </span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Service Detail */}
      {canViewServiceDetail && <Card padding="none" className="overflow-hidden" data-id="element-2552">
          <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-2553" />
          <div className="p-4 md:p-6" data-id="element-2554">
            <CardHeader className="px-0 pt-0" data-id="element-2555">
              <div className="flex items-center justify-between" data-id="element-2556">
                <CardTitle className="flex items-center gap-2" data-id="element-2557">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30" data-id="element-2558">
                    <ListIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-2559" />
                  </div>
                  Detalle por Servicio
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-2560">
                  {canDownloadPdf && serviceDetailStats.details.length > 0 && <button onClick={() => {
                exportServiceDetailToPdf(serviceDetailStats.details, serviceDetailStats.grandTotalRevenue, serviceDetailStats.grandTotalCount);
              }} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2561">
                      <DownloadIcon className="w-4 h-4" data-id="element-2562" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('services')} className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" data-id="element-2563">
                    Ver servicios
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2564" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2565">
              {serviceDetailStats.details.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2566">
                  <BriefcaseIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2567" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2568">
                    No hay citas completadas aún
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2569">
                    Los datos aparecerán cuando se completen citas
                  </p>
                </div> : <div className="overflow-x-auto" data-id="element-2570">
                  <table className="w-full" data-id="element-2571">
                    <thead data-id="element-2572">
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2573">
                        <SortableHeader label="Servicio" sortKey="name" currentDirection={serviceIsSorted('name')} onSort={serviceRequestSort} data-id="element-2574" />
                        <SortableHeader label="Cantidad" sortKey="count" currentDirection={serviceIsSorted('count')} onSort={serviceRequestSort} align="center" data-id="element-2575" />
                        <SortableHeader label="Ingresos Totales" sortKey="totalRevenue" currentDirection={serviceIsSorted('totalRevenue')} onSort={serviceRequestSort} align="right" data-id="element-2576" />
                        <SortableHeader label="Promedio / Cita" sortKey="avgPerAppointment" currentDirection={serviceIsSorted('avgPerAppointment')} onSort={serviceRequestSort} align="right" data-id="element-2577" />
                        <SortableHeader label="% del Total" sortKey="percentOfTotal" currentDirection={serviceIsSorted('percentOfTotal')} onSort={serviceRequestSort} align="right" data-id="element-2578" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2579">
                      {sortedServiceDetails.map((service, index) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2580">
                          <td className="py-3 px-4" data-id="element-2581">
                            <div className="flex items-center gap-3" data-id="element-2582">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2583">
                                {service.name.charAt(0)}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2584">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center" data-id="element-2585">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm font-semibold" data-id="element-2586">
                              {service.count}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-2587">
                            {formatCurrency(service.totalRevenue)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-600 dark:text-gray-400" data-id="element-2588">
                            {formatCurrency(service.avgPerAppointment)}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-2589">
                            <div className="flex items-center justify-end gap-2" data-id="element-2590">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2591">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500" style={{
                          width: `${Math.min(service.percentOfTotal, 100)}%`
                        }} data-id="element-2592" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-right" data-id="element-2593">
                                {service.percentOfTotal.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot data-id="element-2594">
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/80" data-id="element-2595">
                        <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white" data-id="element-2596">
                          TOTAL
                        </td>
                        <td className="py-3 px-4 text-center text-sm font-bold text-gray-900 dark:text-white" data-id="element-2597">
                          {serviceDetailStats.grandTotalCount}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2598">
                          {formatCurrency(serviceDetailStats.grandTotalRevenue)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-600 dark:text-gray-400" data-id="element-2599">
                          {serviceDetailStats.grandTotalCount > 0 ? formatCurrency(serviceDetailStats.grandTotalRevenue / serviceDetailStats.grandTotalCount) : formatCurrency(0)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2600">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
            </CardContent>
          </div>
        </Card>}

      {/* No sections visible */}
      {!canViewStats && !canViewCalendar && !canViewPipeline && !canViewAppointmentsByUser && !canViewRevenueByUser && !canViewRevenueByDoctor && !canViewRecentAppointments && !canViewServiceDetail && <Card className="overflow-hidden" data-id="element-2601">
            <div className="py-16 text-center" data-id="element-2602">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 flex items-center justify-center" data-id="element-2603">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2604">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-2605" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-2606" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-2607" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2608">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'} al
                Panel de Control
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-2609">
                No tienes acceso a las secciones avanzadas del panel. Contacta
                al administrador para solicitar permisos adicionales.
              </p>
            </div>
          </Card>}
    </div>;
}